import type { LibrarySection, ElementTemplateKey } from "./types";
import { getT } from "../../locales";

export const TEMPLATE_MIME = "application/x-qe-template";

export function getElementLibrarySections(): LibrarySection[] {
  const t = getT();
  return [
    {
      id: "basic",
      title: t("constants.section.basic"),
      items: [
        { id: "text-basic", label: t("constants.element.textBlock"), template: "text-basic" },
        { id: "image-basic", label: t("constants.element.image"), template: "image-basic" },
        { id: "audio-basic", label: t("constants.element.audioFile"), template: "audio-basic" },
        { id: "slide-basic", label: t("constants.element.slideVideo"), template: "slide-basic" },
        {
          id: "mathtype-basic",
          label: t("constants.element.mathEditor"),
          template: "mathtype-basic",
        },
        {
          id: "shape-basic",
          label: t("constants.element.shape"),
          template: "shape-basic",
        },
        {
          id: "color-palette-basic",
          label: t("constants.element.colorPalette"),
          template: "color-palette-basic",
        },
        {
          id: "fill-in-the-blanks-basic",
          label: t("constants.element.fillInTheBlanks"),
          template: "fill-in-the-blanks-basic",
        },
        // { id: 'timer-basic', label: '...', template: 'timer-basic' },
        // { id: 'submit-button-basic', label: '...', template: 'submit-button-basic' },
      ],
    },
    {
      id: "formElements",
      title: t("constants.section.formElements"),
      items: [
        { id: "choice-basic", label: t("constants.element.multipleChoice"), template: "choice-basic" },
        { id: "table-basic", label: t("constants.element.table"), template: "table-basic" },
        {
          id: "drag-source-basic",
          label: t("constants.element.draggableItem"),
          template: "drag-source-basic",
        },
        {
          id: "drop-target-basic",
          label: t("constants.element.targetArea"),
          template: "drop-target-basic",
        },
        {
          id: "order-list-basic",
          label: t("constants.element.orderingArea"),
          template: "order-list-basic",
        },
        {
          id: "matching-point-source-basic",
          label: t("constants.element.sourcePoint"),
          template: "matching-point-source-basic",
        },
        {
          id: "matching-point-target-basic",
          label: t("constants.element.targetPoint"),
          template: "matching-point-target-basic",
        },
        {
          id: "matching-2-pairs",
          label: t("constants.element.matching2Pairs"),
          template: "matching-2-pairs",
        },
        {
          id: "matching-3-pairs",
          label: t("constants.element.matching3Pairs"),
          template: "matching-3-pairs",
        },
        {
          id: "matching-4-pairs",
          label: t("constants.element.matching4Pairs"),
          template: "matching-4-pairs",
        },
        {
          id: "paint-area-basic",
          label: t("constants.element.paintArea"),
          template: "paint-area-basic",
        },
        {
          id: "paint-swatch-basic",
          label: t("constants.element.colorSwatch"),
          template: "paint-swatch-basic",
        },
        {
          id: "push-to-talk-basic",
          label: t("constants.element.pushToTalk"),
          template: "push-to-talk-basic",
        },
        {
          id: "count-area-basic",
          label: t("constants.element.countArea"),
          template: "count-area-basic",
        },
        {
          id: "keypad-1-5",
          label: t("constants.element.keypadOneToFive"),
          template: "keypad-1-5",
        },
        {
          id: "keypad-0-9",
          label: t("constants.element.keypadZeroToNine"),
          template: "keypad-0-9",
        },
        {
          id: "keypad-math",
          label: t("constants.element.keypadMath"),
          template: "keypad-math",
        },
        {
          id: "play-button-basic",
          label: t("constants.element.playButton"),
          template: "play-button-basic",
        },
        {
          id: "cube-bar-5",
          label: t("constants.element.cubeBar5"),
          template: "cube-bar-5",
        },
        {
          id: "cube-bar-10",
          label: t("constants.element.cubeBar10"),
          template: "cube-bar-10",
        },
        {
          id: "cube-source-basic",
          label: t("constants.element.cubeSource"),
          template: "cube-source-basic",
        },
      ],
    },
  ];
}

export function getAllElementTemplates(): ElementTemplateKey[] {
  return Array.from(
    new Set(
      getElementLibrarySections().flatMap((section) =>
        section.items
          .map((item) => item.template)
          .filter((template): template is ElementTemplateKey => Boolean(template))
      )
    )
  );
}

const MULTIPLE_CHOICE_TEMPLATES: ElementTemplateKey[] = [
  "choice-basic",
  "table-basic",
  "text-basic",
  "image-basic",
  "audio-basic",
  "slide-basic",
  "mathtype-basic",
  "mathlive-basic",
];

const DRAG_DROP_TEMPLATES: ElementTemplateKey[] = [
  "drag-source-basic",
  "drop-target-basic",
  "table-basic",
];

// Tıklayarak Sayma: yalnızca interaktif sayma alanı elemanı görünür.
const COUNT_AREA_TEMPLATES: ElementTemplateKey[] = ["count-area-basic"];

// Klavyeler — öğrencinin cevap girmesi için sanal tuş takımları.
const KEYPAD_TEMPLATES: ElementTemplateKey[] = [
  "keypad-1-5",
  "keypad-0-9",
  "keypad-math",
];

// Boşluk Doldurma: cevap boşluğu + öğrencinin sayı/işlem girebileceği
// klavyeler.
const FILL_IN_THE_BLANKS_TEMPLATES: ElementTemplateKey[] = [
  "fill-in-the-blanks-basic",
  "table-basic",
  ...KEYPAD_TEMPLATES,
];

// Dinleyerek Cevaplama: Oynat (Sesli Metin) butonu + öğrencinin cevabı
// gireceği çoktan seçmeli / boşluk doldurma / klavyeler + tablo. Sürükle-
// bırak / eşleştirme / sıralama gibi yazma dışı araçlar gizlenir.
const LISTENING_TEMPLATES: ElementTemplateKey[] = [
  "play-button-basic",
  "choice-basic",
  "fill-in-the-blanks-basic",
  "table-basic",
  ...KEYPAD_TEMPLATES,
];

// Lego Toplama-Çıkarma: hedef küp çubuğu (5/10) + sınırsız küp kaynağı +
// cevabın girileceği araçlar. Sürükle-bırak/eşleştirme/sıralama gizli.
const CUBE_TEMPLATES: ElementTemplateKey[] = [
  "cube-bar-5",
  "cube-bar-10",
  "cube-source-basic",
  "choice-basic",
  "fill-in-the-blanks-basic",
  "table-basic",
  ...KEYPAD_TEMPLATES,
];

const ORDER_LIST_TEMPLATES: ElementTemplateKey[] = [
  "order-list-basic",
  "table-basic",
];

const MATCHING_TEMPLATES: ElementTemplateKey[] = [
  "matching-point-source-basic",
  "matching-point-target-basic",
  "matching-2-pairs",
  "matching-3-pairs",
  "matching-4-pairs",
  "table-basic",
  "text-basic",
  "image-basic",
  "audio-basic",
  "slide-basic",
  "mathtype-basic",
  "mathlive-basic",
];

export function getQuestionTypeLibraryFilters(): Record<
  string,
  ElementTemplateKey[]
> {
  return {
    default: getAllElementTemplates(),
    "multiple-choice": MULTIPLE_CHOICE_TEMPLATES,
    multiplechoice: MULTIPLE_CHOICE_TEMPLATES,
    multiple_choice: MULTIPLE_CHOICE_TEMPLATES,
    "multiplechoice-single": MULTIPLE_CHOICE_TEMPLATES,
    "coktan-secmeli": MULTIPLE_CHOICE_TEMPLATES,
    "coktan-secmeli-soru": MULTIPLE_CHOICE_TEMPLATES,
    "single-choice": MULTIPLE_CHOICE_TEMPLATES,
    "surukle-birak": DRAG_DROP_TEMPLATES,
    "drag-drop": DRAG_DROP_TEMPLATES,
    drag_drop: DRAG_DROP_TEMPLATES,
    "drag-and-drop": DRAG_DROP_TEMPLATES,
    draganddrop: DRAG_DROP_TEMPLATES,
    dragdrop: DRAG_DROP_TEMPLATES,
    matching: MATCHING_TEMPLATES,
    eslestirme: MATCHING_TEMPLATES,
    "order-list": ORDER_LIST_TEMPLATES,
    ordering: ORDER_LIST_TEMPLATES,
    sorting: ORDER_LIST_TEMPLATES,
    siralama: ORDER_LIST_TEMPLATES,
    Sıralama: ORDER_LIST_TEMPLATES,
    "s-ralama": ORDER_LIST_TEMPLATES,
    siralamasorusu: ORDER_LIST_TEMPLATES,
    "fill-in-the-blanks": FILL_IN_THE_BLANKS_TEMPLATES,
    "bosluk-doldurma": FILL_IN_THE_BLANKS_TEMPLATES,
    boslukdoldurma: FILL_IN_THE_BLANKS_TEMPLATES,
    bosluk_doldurma: FILL_IN_THE_BLANKS_TEMPLATES,
    // Dinleyerek Cevaplama ("Dinleyerek Cevaplama" → "dinleyerek-cevaplama").
    "dinleyerek-cevaplama": LISTENING_TEMPLATES,
    dinleyerekcevaplama: LISTENING_TEMPLATES,
    "dinleyerek-cevap": LISTENING_TEMPLATES,
    dinleme: LISTENING_TEMPLATES,
    "listen-and-answer": LISTENING_TEMPLATES,
    listenandanswer: LISTENING_TEMPLATES,
    listen_and_answer: LISTENING_TEMPLATES,
    listening: LISTENING_TEMPLATES,
    listen: LISTENING_TEMPLATES,
    // Lego Toplama-Çıkarma. NOT: "Çıkarma" -> ç=c, ı=-; "Lego Toplama-
    // Çıkarma" -> "lego-toplama-c-karma".
    "lego-toplama-c-karma": CUBE_TEMPLATES,
    "lego-toplam-c-karma": CUBE_TEMPLATES,
    "lego-toplama-cikarma": CUBE_TEMPLATES,
    "lego-toplama": CUBE_TEMPLATES,
    "toplama-cikarma": CUBE_TEMPLATES,
    "topla-cikar": CUBE_TEMPLATES,
    "kup-toplama": CUBE_TEMPLATES,
    "add-subtract": CUBE_TEMPLATES,
    addsubtract: CUBE_TEMPLATES,
    "cube-add-subtract": CUBE_TEMPLATES,
    cubeaddsubtract: CUBE_TEMPLATES,
    lego: CUBE_TEMPLATES,
    painting: ["paint-area-basic", "color-palette-basic"],
    boyama: ["paint-area-basic", "color-palette-basic"],
    paint: ["paint-area-basic", "color-palette-basic"],
    "bas-konuş": ["push-to-talk-basic"],
    bas_konuş: ["push-to-talk-basic"],
    basKonuş: ["push-to-talk-basic"],
    "bas-konus": ["push-to-talk-basic"],
    bas_konus: ["push-to-talk-basic"],
    basKonus: ["push-to-talk-basic"],
    "push-to-talk": ["push-to-talk-basic"],
    pushtotalk: ["push-to-talk-basic"],
    push_to_talk: ["push-to-talk-basic"],
    pushToTalk: ["push-to-talk-basic"],
    // Tıklayarak Sayma. NOT: normalizeQuestionTypeKey, Türkçe "ı" harfini
    // (ASCII olmadığı için) "-"'ye çevirir; "Tıklayarak Say(ma)" →
    // "t-klayarak-say(ma)". Asıl eşleşen anahtarlar bunlardır.
    "t-klayarak-say": COUNT_AREA_TEMPLATES,
    "t-klayarak-sayma": COUNT_AREA_TEMPLATES,
    "tiklayarak-say": COUNT_AREA_TEMPLATES,
    "tiklayarak-sayma": COUNT_AREA_TEMPLATES,
    "click-count": COUNT_AREA_TEMPLATES,
    clickcount: COUNT_AREA_TEMPLATES,
    click_count: COUNT_AREA_TEMPLATES,
    counting: COUNT_AREA_TEMPLATES,
    sayma: COUNT_AREA_TEMPLATES,
  };
}
